// src/types/result.ts
var ok = (data) => ({ ok: true, data });
var err = (error) => ({ ok: false, error });
async function tryAsync(fn) {
  try {
    return ok(await fn());
  } catch (e) {
    return err(e instanceof Error ? e.message : String(e));
  }
}

// src/utils/attributeExtractor.ts
import { AttributeType } from "@propeller-commerce/propeller-sdk-v2";
function attributeNameMatches(attr, targetName) {
  const desc = attr.attributeDescription;
  if (!desc) return false;
  if (desc.name === targetName) return true;
  if (desc.descriptions?.some((d) => d.value === targetName)) return true;
  return false;
}
function getAttributeDisplayName(attr, language) {
  const desc = attr.attributeDescription;
  if (!desc) return "";
  if (language && desc.descriptions?.length) {
    const lang = language.toUpperCase();
    const match = desc.descriptions.find((d) => d.language?.toUpperCase() === lang);
    if (match?.value) return match.value;
  }
  return desc.descriptions?.[0]?.value ?? desc.name ?? "";
}
function extractAttributeValues(attr) {
  const values = [];
  const v = attr.value;
  if (!v) return values;
  if (v.colorValue) {
    values.push(v.colorValue);
  } else if (Array.isArray(v.textValues)) {
    const firstNonEmpty = v.textValues.find(
      (entry) => Array.isArray(entry?.values) && entry.values.length > 0
    );
    const list = firstNonEmpty?.values ?? [];
    return list.filter(Boolean);
  } else if (v.textValue) {
    values.push(v.textValue);
  } else if (v.numericValue !== void 0) {
    values.push(String(v.numericValue));
  } else if (v.booleanValue !== void 0) {
    values.push(v.booleanValue ? "Yes" : "No");
  } else if (v.type === AttributeType.COLOR) {
    if (v.value) values.push(v.value);
  } else if (v.type === AttributeType.TEXT) {
    const buckets = v.value?.textValues ?? [];
    const firstNonEmpty = buckets.find(
      (entry) => Array.isArray(entry?.values) && entry.values.length > 0
    );
    const textVals = firstNonEmpty?.values ?? [];
    return textVals.filter(Boolean);
  } else if (v.type === AttributeType.DECIMAL) {
    if (v.value !== void 0) values.push(String(v.value));
  } else if (v.type === AttributeType.INT) {
    if (v.value !== void 0) values.push(String(v.value));
  } else if (v.type === AttributeType.ENUM) {
    if (Array.isArray(v.enumValues)) {
      return v.enumValues.filter(Boolean);
    }
    if (v.value) values.push(v.value);
  } else if (typeof v === "string") {
    values.push(v);
  } else if (typeof v === "object") {
    if (Array.isArray(v.values)) {
      return v.values.filter((x) => typeof x === "string");
    }
    const strVals = Object.values(v).filter((x) => typeof x === "string");
    return strVals;
  }
  return values.filter(Boolean);
}
function collectAttributeValues(products, attributeName) {
  const set = /* @__PURE__ */ new Set();
  for (const product of products) {
    const items = product.attributes?.items;
    if (!Array.isArray(items)) continue;
    for (const attr of items) {
      if (attributeNameMatches(attr, attributeName)) {
        extractAttributeValues(attr).forEach((v) => set.add(v));
      }
    }
  }
  return Array.from(set);
}
function filterProductsBySelections(products, selections) {
  const entries = Object.entries(selections);
  if (entries.length === 0) return products;
  return products.filter((product) => {
    const items = product.attributes?.items;
    if (!Array.isArray(items)) return false;
    return entries.every(
      ([attrName, attrValue]) => items.some(
        (attr) => attributeNameMatches(attr, attrName) && extractAttributeValues(attr).includes(attrValue)
      )
    );
  });
}

// src/utils/countries.ts
var COUNTRIES = [
  { code: "NL", name: "Netherlands" },
  { code: "BE", name: "Belgium" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "UK", name: "United Kingdom" },
  { code: "US", name: "United States" }
];
var COUNTRIES_MAP = COUNTRIES.reduce(
  (acc, c) => {
    acc[c.code] = c.name;
    return acc;
  },
  {}
);
function getCountryName(code, list) {
  if (!code) return "";
  const effective = list && list.length > 0 ? list : COUNTRIES;
  const match = effective.find((c) => c.code === code);
  return match?.name ?? code;
}

// src/utils/orderHelpers.ts
import { OrderItemClass, YesNo } from "@propeller-commerce/propeller-sdk-v2";
function getNettedBonusItems(items) {
  const all = items || [];
  const discountsByParent = /* @__PURE__ */ new Map();
  for (const item of all) {
    if (item.class !== OrderItemClass.incentive || !item.parentOrderItemId) continue;
    const acc = discountsByParent.get(item.parentOrderItemId) || { total: 0, totalNet: 0 };
    acc.total += item.priceTotal || 0;
    acc.totalNet += item.priceTotalNet ?? item.priceTotal ?? 0;
    discountsByParent.set(item.parentOrderItemId, acc);
  }
  return all.filter((item) => item.class === OrderItemClass.product && item.isBonus === YesNo.Y).map((item) => {
    const discount = discountsByParent.get(item.id);
    if (!discount) return item;
    const netted = { ...item, priceTotal: (item.priceTotal || 0) + discount.total };
    if (item.priceTotalNet !== void 0 && item.priceTotalNet !== null) {
      netted.priceTotalNet = item.priceTotalNet + discount.totalNet;
    }
    return netted;
  });
}

// src/utils/formatting.ts
function localeForLanguage(language) {
  if (!language) return "nl-NL";
  if (language.includes("-")) return language;
  const known = {
    NL: "nl-NL",
    EN: "en-GB",
    DE: "de-DE",
    FR: "fr-FR",
    ES: "es-ES",
    IT: "it-IT",
    BE: "nl-BE"
  };
  return known[language.toUpperCase()] ?? "nl-NL";
}
function formatPrice(amount, options = {}) {
  if (amount === null || amount === void 0) return "";
  const { currency = "EUR", locale = "nl-NL", symbol } = options;
  if (symbol !== void 0) {
    const formattedNumber = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Number(amount));
    if (!symbol) return formattedNumber;
    try {
      const parts = new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).formatToParts(Number(amount));
      if (parts.some((part) => part.type === "currency")) {
        return parts.map((part) => part.type === "currency" ? symbol : part.value).join("").replace(/ /g, " ");
      }
    } catch {
    }
    return `${symbol} ${formattedNumber}`;
  }
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  } catch {
    return `\u20AC${Number(amount).toFixed(2)}`;
  }
}
function formatDate(date, options = {}) {
  if (!date) return "";
  const { locale = "nl-NL", dateStyle = "medium" } = options;
  try {
    const d = typeof date === "string" ? new Date(date) : date;
    if (isNaN(d.getTime())) return "";
    return new Intl.DateTimeFormat(locale, { dateStyle }).format(d);
  } catch {
    return String(date);
  }
}
function formatSurcharge(surcharge, options = {}) {
  if (!surcharge) return "";
  const { quantity = 1, language, currency = "\u20AC", locale = "nl-NL" } = options;
  const localized = surcharge.names ?? surcharge.name ?? [];
  const name = localized.find((n) => n.language === language)?.value ?? localized[0]?.value ?? "";
  const value = Number(surcharge.value ?? 0);
  const isPercentage = String(surcharge.type ?? "").toLowerCase() === "percentage";
  if (isPercentage) {
    return `${quantity} x ${value}% (${name})`;
  }
  return `${quantity} x ${formatPrice(value, { symbol: currency, locale })} (${name})`;
}
function calcDiscountPercent(original, discounted) {
  if (!original || original === 0) return 0;
  if (discounted === null || discounted === void 0) return 0;
  return Math.round((original - discounted) / original * 100);
}

// src/utils/inventoryHelpers.ts
function getStockStatus(quantity) {
  if (quantity <= 0) return { label: "Out of stock", className: "text-destructive bg-destructive/10" };
  if (quantity <= 5) return { label: "Low stock", className: "text-warning bg-warning/10" };
  return { label: "In stock", className: "text-success bg-success/10" };
}
var MIN_STOCK_THRESHOLD = 1;
function buildInventoryFilter(sel, minQuantity) {
  if (sel !== "in-stock") return void 0;
  const n = Math.max(MIN_STOCK_THRESHOLD, Math.floor(minQuantity ?? MIN_STOCK_THRESHOLD));
  return { totalQuantity: { greaterThan: n } };
}

// src/utils/labelHelpers.ts
function getLabel(labels, key, fallback) {
  const value = labels?.[key];
  if (value !== void 0 && value !== null) return value;
  warnMissingLabel(key, fallback);
  return fallback;
}
var warnedKeys = /* @__PURE__ */ new Set();
function warnMissingLabel(key, fallback) {
  if (process.env.NODE_ENV !== "development") return;
  if (warnedKeys.has(key)) return;
  warnedKeys.add(key);
  console.warn(
    `[propeller] Missing label "${key}" \u2014 falling back to "${fallback}". Add it to the locale dictionary for this component's namespace.`
  );
}

// src/utils/languageResolver.ts
function resolveLanguageEntry(items, language, fallback) {
  if (!items || items.length === 0) return void 0;
  const lang = language.toUpperCase();
  return items.find((item) => item.language?.toUpperCase() === lang) ?? items[0];
}
function getLanguageString(items, language, fallback = "") {
  if (!items || items.length === 0) return fallback;
  const lang = language.toUpperCase();
  const direct = items.find((item) => item.language?.toUpperCase() === lang);
  if (direct?.value) return direct.value;
  for (const item of items) {
    if (item.value) return item.value;
  }
  return fallback;
}
function getLanguageUri(items, language, fallback = "") {
  const entry = resolveLanguageEntry(items, language);
  return entry?.uri ?? fallback;
}

// src/utils/productHelpers.ts
function getProductImageUrl(product) {
  return product?.media?.images?.items?.[0]?.imageVariants?.[0]?.url || "";
}
function getClusterImageUrl(cluster) {
  return cluster?.defaultProduct?.media?.images?.items?.[0]?.imageVariants?.[0]?.url || "";
}
function getProductSku(product) {
  return product?.sku || "";
}
function getClusterSku(cluster) {
  return cluster?.sku || cluster?.defaultProduct?.sku || "";
}
function getLocalizedValue(items, language, fallback = "") {
  return getLanguageString(items, language || "NL", fallback);
}

// src/utils/truncation.ts
function stripHtml(html) {
  return html.replace(/<[^>]*>/g, "");
}
function shouldTruncate(html, maxLength) {
  if (!maxLength || maxLength <= 0) return false;
  const plain = stripHtml(html);
  return plain.length > maxLength;
}
function truncateAt(html, maxLength) {
  if (!maxLength || maxLength <= 0) return html;
  const plain = stripHtml(html);
  if (plain.length <= maxLength) return html;
  const truncated = plain.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(" ");
  return (lastSpace > 0 ? truncated.substring(0, lastSpace) : truncated) + "\u2026";
}

// src/utils/userIdentity.ts
function isContact(user) {
  return !!user && "contactId" in user;
}
function isCustomer(user) {
  return !!user && "customerId" in user && !("contactId" in user);
}
function getUserId(user) {
  if (!user) return null;
  if (isContact(user)) return user.contactId ?? null;
  if (isCustomer(user)) return user.customerId ?? null;
  return null;
}
function getCompany(user) {
  if (!user || !isContact(user)) return null;
  return user.company ?? null;
}
function getCompanyId(user) {
  return getCompany(user)?.companyId ?? null;
}
function getAddresses(user) {
  if (!user) return [];
  if (isContact(user)) {
    return user.company?.addresses ?? [];
  }
  return user.addresses ?? [];
}
function getDefaultInvoiceAddress(user) {
  return getAddresses(user).find(
    (addr) => addr.isDefault === "Y" && addr.type === "invoice"
  );
}
function getDefaultDeliveryAddress(user) {
  return getAddresses(user).find(
    (addr) => addr.isDefault === "Y" && addr.type === "delivery"
  );
}

// src/utils/videoTransform.ts
function isEmbeddable(uri) {
  return uri.includes("youtube.com") || uri.includes("youtu.be") || uri.includes("vimeo.com");
}
function normalizeVideoUrl(uri) {
  if (uri.includes("youtube.com/watch")) {
    try {
      const url = new URL(uri);
      const videoId = url.searchParams.get("v") || "";
      return `https://www.youtube.com/embed/${videoId}`;
    } catch {
      return uri;
    }
  }
  if (uri.includes("youtu.be/")) {
    const videoId = uri.split("youtu.be/")[1]?.split("?")[0] || "";
    return `https://www.youtube.com/embed/${videoId}`;
  }
  if (uri.includes("vimeo.com/")) {
    const videoId = uri.split("vimeo.com/")[1]?.split("?")[0] || "";
    return `https://player.vimeo.com/video/${videoId}`;
  }
  return uri;
}

// src/utils/visibilityHelpers.ts
function isContentHidden(portalMode, user, isAuthenticated) {
  return portalMode === "semi-closed" && !user && !isAuthenticated;
}

// src/utils/purchaseAuthorization.ts
var PURCHASER = "PURCHASER";
function field(source, name) {
  if (!source || typeof source !== "object") return void 0;
  const bag = source;
  return bag[name] ?? bag[`_${name}`];
}
function findPurchaserPac(user, companyId) {
  if (!user || !("contactId" in user) || companyId == null) return void 0;
  const configs = field(user, "purchaseAuthorizationConfigs");
  const items = field(configs, "items") ?? [];
  return items.find((pac) => {
    if (field(pac, "purchaseRole") !== PURCHASER) return false;
    const company = field(pac, "company");
    return Number(field(company, "companyId")) === Number(companyId);
  });
}
function isOverAuthorizationLimit(user, companyId, cart) {
  if (!cart) return false;
  const pac = findPurchaserPac(user, companyId);
  if (!pac) return false;
  const limit = field(pac, "authorizationLimit") ?? 0;
  const total = field(cart, "total");
  const totalGross = field(total, "totalGross") ?? 0;
  return totalGross > limit;
}
function isCheckoutAllowed(user, companyId, cart) {
  return !isOverAuthorizationLimit(user, companyId, cart);
}

// src/utils/jsonLd.ts
import { ProductClass } from "@propeller-commerce/propeller-sdk-v2";
function safeJsonStringify(value) {
  return JSON.stringify(value).replace(/<\//g, "<\\/");
}
function shouldShowOffers(ctx) {
  return !isContentHidden(ctx.portalMode, ctx.user);
}
function toAbsolute(siteUrl, pathOrUrl) {
  if (!pathOrUrl) return "";
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const base = siteUrl.replace(/\/$/, "");
  const path = pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`;
  return base + path;
}
function formatPriceForJsonLd(amount) {
  if (amount == null || !Number.isFinite(amount)) return void 0;
  return amount.toFixed(2);
}
function cleanDescription(value) {
  if (!value) return void 0;
  const text = stripHtml(value).replace(/\s+/g, " ").trim();
  return text.length > 0 ? text : void 0;
}
function buildOffers(product, absoluteUrl, ctx) {
  if (!shouldShowOffers(ctx)) return void 0;
  const price = formatPriceForJsonLd(product.price?.net);
  if (price === void 0) return void 0;
  const offer = {
    "@type": "Offer",
    url: absoluteUrl,
    priceCurrency: ctx.currencyCode,
    price,
    itemCondition: "https://schema.org/NewCondition"
  };
  const qty = product.inventory?.totalQuantity;
  if (typeof qty === "number") {
    offer.availability = qty > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock";
  }
  return offer;
}
function buildProductJsonLd(product, ctx) {
  if (!product) return null;
  const name = getLanguageString(product.names, ctx.language, "").trim();
  const relativeUrl = ctx.urls.getProductUrl(product, ctx.language);
  const absoluteUrl = toAbsolute(ctx.siteUrl, relativeUrl);
  if (!name && !absoluteUrl) return null;
  const description = cleanDescription(
    getLanguageString(product.descriptions, ctx.language, "") || getLanguageString(product.shortDescriptions, ctx.language, "")
  );
  const imageUrl = getProductImageUrl(product);
  const absoluteImage = imageUrl ? toAbsolute(ctx.siteUrl, imageUrl) : void 0;
  const categoryName = getLanguageString(product.category?.names, ctx.language, "").trim();
  const data = {
    "@context": "https://schema.org/",
    "@type": "Product"
  };
  if (name) data.name = name;
  if (absoluteUrl) data.url = absoluteUrl;
  if (absoluteImage) data.image = absoluteImage;
  if (description) data.description = description;
  if (product.manufacturerCode) data.mpn = product.manufacturerCode;
  if (product.sku) data.sku = product.sku;
  if (product.productId != null) data.productID = String(product.productId);
  if (categoryName) data.category = categoryName;
  if (product.manufacturer) {
    data.brand = { "@type": "Brand", name: product.manufacturer };
  }
  const offers = buildOffers(product, absoluteUrl, ctx);
  if (offers) data.offers = offers;
  return data;
}
function buildClusterJsonLd(cluster, ctx) {
  if (!cluster) return null;
  const defaultProduct = cluster.defaultProduct;
  const name = getLanguageString(cluster.names, ctx.language, "").trim();
  const relativeUrl = ctx.urls.getClusterUrl(cluster, ctx.language);
  const absoluteUrl = toAbsolute(ctx.siteUrl, relativeUrl);
  if (!name && !absoluteUrl) return null;
  const description = cleanDescription(
    getLanguageString(cluster.descriptions, ctx.language, "")
  );
  const imageUrl = getClusterImageUrl(cluster);
  const absoluteImage = imageUrl ? toAbsolute(ctx.siteUrl, imageUrl) : void 0;
  const categoryName = getLanguageString(cluster.category?.names, ctx.language, "").trim();
  const data = {
    "@context": "https://schema.org/",
    "@type": "Product"
  };
  if (name) data.name = name;
  if (absoluteUrl) data.url = absoluteUrl;
  if (absoluteImage) data.image = absoluteImage;
  if (description) data.description = description;
  if (defaultProduct?.manufacturerCode) data.mpn = defaultProduct.manufacturerCode;
  const sku = defaultProduct?.sku;
  if (sku) data.sku = sku;
  if (cluster.clusterId != null) data.productID = String(cluster.clusterId);
  if (categoryName) data.category = categoryName;
  if (defaultProduct?.manufacturer) {
    data.brand = { "@type": "Brand", name: defaultProduct.manufacturer };
  }
  if (defaultProduct) {
    const offers = buildOffers(defaultProduct, absoluteUrl, ctx);
    if (offers) data.offers = offers;
  }
  return data;
}
function buildItemListJsonLd(products, ctx) {
  if (!products || products.length === 0) return null;
  const itemListElement = [];
  products.forEach((item, index) => {
    const isCluster = item.type === ProductClass.CLUSTER;
    const productForFields = isCluster ? item.defaultProduct : item;
    if (!productForFields) return;
    const cluster = isCluster ? item : void 0;
    const relativeUrl = cluster ? ctx.urls.getClusterUrl(cluster, ctx.language) : ctx.urls.getProductUrl(item, ctx.language);
    const absoluteUrl = toAbsolute(ctx.siteUrl, relativeUrl);
    const productNode = buildProductJsonLd(productForFields, ctx);
    if (!productNode) return;
    if (absoluteUrl) productNode.url = absoluteUrl;
    if (productNode.offers && typeof productNode.offers === "object") {
      productNode.offers.url = absoluteUrl;
    }
    delete productNode["@context"];
    itemListElement.push({
      "@type": "ListItem",
      position: index + 1,
      item: productNode
    });
  });
  if (itemListElement.length === 0) return null;
  return {
    "@context": "https://schema.org/",
    "@type": "ItemList",
    numberOfItems: itemListElement.length,
    itemListElement
  };
}

// src/utils/userMode.ts
function deriveUserMode(user, shopMode) {
  if (!user) return "anonymous";
  if (shopMode === "b2b") return "b2b";
  if (shopMode === "b2c") return "b2c";
  return isContact(user) ? "b2b" : "b2c";
}

// src/services/createServices.ts
import {
  ProductService,
  CartService,
  UserService,
  CategoryService,
  OrderService,
  PayMethodService,
  LoginService,
  AddressService,
  CompanyService,
  CrossupsellService,
  BundleService,
  FavoriteListService,
  PurchaseAuthorizationConfigService,
  ClusterService,
  OrderlistService
} from "@propeller-commerce/propeller-sdk-v2";
var servicesByClient = /* @__PURE__ */ new WeakMap();
function createServices(client) {
  const cached = servicesByClient.get(client);
  if (cached) return cached;
  const services = {
    product: new ProductService(client),
    cart: new CartService(client),
    user: new UserService(client),
    category: new CategoryService(client),
    order: new OrderService(client),
    payMethod: new PayMethodService(client),
    login: new LoginService(client),
    address: new AddressService(client),
    company: new CompanyService(client),
    crossupsell: new CrossupsellService(client),
    bundle: new BundleService(client),
    favoriteList: new FavoriteListService(client),
    purchaseAuthConfig: new PurchaseAuthorizationConfigService(client),
    cluster: new ClusterService(client),
    orderlist: new OrderlistService(client)
  };
  servicesByClient.set(client, services);
  return services;
}

// src/services/toPlain.ts
function toPlain(value) {
  return deepPlainInner(value);
}
function deepPlainInner(value) {
  if (value === null || value === void 0) return value;
  if (Array.isArray(value)) return value.map(deepPlainInner);
  if (typeof value === "object") {
    const out = {};
    for (const key of Object.keys(value)) {
      const cleanKey = key.startsWith("_") ? key.slice(1) : key;
      out[cleanKey] = deepPlainInner(value[key]);
    }
    return out;
  }
  return value;
}
export {
  COUNTRIES,
  COUNTRIES_MAP,
  MIN_STOCK_THRESHOLD,
  attributeNameMatches,
  buildClusterJsonLd,
  buildInventoryFilter,
  buildItemListJsonLd,
  buildProductJsonLd,
  calcDiscountPercent,
  collectAttributeValues,
  createServices,
  deriveUserMode,
  err,
  extractAttributeValues,
  filterProductsBySelections,
  findPurchaserPac,
  formatDate,
  formatPrice,
  formatSurcharge,
  getAddresses,
  getAttributeDisplayName,
  getClusterImageUrl,
  getClusterSku,
  getCompany,
  getCompanyId,
  getCountryName,
  getDefaultDeliveryAddress,
  getDefaultInvoiceAddress,
  getLabel,
  getLanguageString,
  getLanguageUri,
  getLocalizedValue,
  getNettedBonusItems,
  getProductImageUrl,
  getProductSku,
  getStockStatus,
  getUserId,
  isCheckoutAllowed,
  isContact,
  isContentHidden,
  isCustomer,
  isEmbeddable,
  isOverAuthorizationLimit,
  localeForLanguage,
  normalizeVideoUrl,
  ok,
  resolveLanguageEntry,
  safeJsonStringify,
  shouldTruncate,
  stripHtml,
  toPlain,
  truncateAt,
  tryAsync
};
//# sourceMappingURL=index.js.map